import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, afterRenderEffect, inject, untracked, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLinkWithHref } from '@angular/router';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CustomMadePriceEstimatesIntroService } from '../../../inquiries/custom-made-price-estimates/custom-made-price-estimates-intro.service';
import { OrderListsViewTypeService } from '../../../orders/services/order-lists-view-type.service';
import { SharedModule } from '../../../shared/shared.module';
import { OrderListsViewTypeEnum } from '../../enums/order-lists-view.enum';
import { InquiriesRoutePath, InquiriesRouteType, OrderListRouteType, RouteTypeEnums } from '../../enums/route-types.enum';
import { UserPermissionsEnum } from '../../enums/user-permissions.enum';
import { PermissionsService } from '../../services/permissions/permissions.service';
import { UserService } from '../../services/user/user.service';
import { isMobile } from '../../util/feature-detection.helper';
import { NavbarElementInterface, NavbarElements } from '../navbar-elements';
import { OrderListsViewTypeSwitchComponent } from '../order-lists-view-switch/order-lists-view-type-switch.component';
import { NavigationSearchInputComponent } from '../search/navigation-search-input.component';
import { SearchService } from '../search/search.service';
import { TreeService } from '../tree.service';

@Component({
  selector: 'app-sub-navbar',
  templateUrl: './sub-navbar.component.html',
  styleUrls: ['./sub-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, NavigationSearchInputComponent, RouterLinkWithHref, OrderListsViewTypeSwitchComponent],
})
export class SubNavbarComponent implements OnInit, OnDestroy {
  orderListsViewService = inject(OrderListsViewTypeService);
  private userService = inject(UserService);
  private treeService = inject(TreeService);
  private searchService = inject(SearchService);
  private permissionsService = inject(PermissionsService);
  private router = inject(Router);
  private customMadePriceEstimatesIntroService = inject(CustomMadePriceEstimatesIntroService);
  private isMobileDevice = isMobile();
  private readonly estimatesIntroPopover = viewChild<NgbPopover>('estimatesIntroPopover');
  readonly estimatesLink = ['/', InquiriesRoutePath.ROOT, InquiriesRoutePath.CUSTOM_MADE_PRICE_ESTIMATES];

  parentElement: NavbarElementInterface;
  elements?: NavbarElementInterface[] = [];
  selectedElement: NavbarElementInterface = null;
  thirdLevelVisible = false;
  selectedElementUrlPath = '';
  topLevelUrlPath = '';
  initialViewAssigned = false;
  routeTypeEnums = RouteTypeEnums;
  orderListsViewTypeEnum = OrderListsViewTypeEnum;
  activeOrderListsViewType: OrderListsViewTypeEnum = OrderListsViewTypeEnum.ORDERS_MANAGEMENT;

  private subscription: Subscription = new Subscription();
  private navigateAfterViewSwitch = false;

  constructor() {
    afterRenderEffect(() => {
      const popover = this.estimatesIntroPopover();
      if (!popover || this.isMobileDevice) {
        return;
      }
      const dismissed = this.customMadePriceEstimatesIntroService.dismissed();
      untracked(() => (dismissed ? popover.close() : popover.open()));
    });
  }

  ngOnInit() {
    this.topLevelUrlPath = this.router.url.split('/')[1]?.split('?')[0];

    this.subscription.add(
      this.router.events.subscribe((routerEvent) => {
        if (routerEvent instanceof NavigationEnd) {
          this.treeService.closeNavbar('third');
          const path = routerEvent.url.split('/');
          this.topLevelUrlPath = path[1]?.split('?')[0];
          this.selectedElementUrlPath = path[2]?.split('?')[0]; // remove possible query params

          // sync selected element from url after route change
          if (this.selectedElementUrlPath?.length) {
            this.selectedElement =
              this.elements.find(
                (element) => element.path === this.selectedElementUrlPath || element.aliasPaths?.includes(this.selectedElementUrlPath),
              ) || null;
          }
        }
      }),
    );

    this.subscription.add(
      combineLatest([
        this.treeService.getState(),
        this.permissionsService.getUserPermissionsAsObservable(),
        this.orderListsViewService.getOrderListsViewTypeAsObservable(),
      ])
        .pipe(
          switchMap(([{ first, second, third }, permissions, orderListsViewType]) => {
            this.parentElement = first;
            this.selectedElement = second;
            this.thirdLevelVisible = third;
            this.activeOrderListsViewType = orderListsViewType;
            // find active route child element based on current url path and active view type
            if (!this.initialViewAssigned && first?.id === RouteTypeEnums.ORDERS) {
              this.initialViewAssigned = true;
              const urlPath = this.router.url.split('?')[0];
              const targetPath = urlPath.split('/')[2];

              const ordersParent = NavbarElements.find((el) => el.id === RouteTypeEnums.ORDERS);
              if (ordersParent?.children && targetPath) {
                const matchesCurrentView =
                  ordersParent.children.find(
                    (element) =>
                      (element.path === targetPath || element.aliasPaths?.includes(targetPath)) &&
                      element.restrictToOrderListsViewType === this.activeOrderListsViewType,
                  ) || null;

                if (!matchesCurrentView) {
                  // Find child element that matches the target path and has a different view type, then switch to that view
                  const child = ordersParent.children.find(
                    (element) => element.path === targetPath || element.aliasPaths?.includes(targetPath),
                  );

                  if (child?.restrictToOrderListsViewType && child.restrictToOrderListsViewType !== this.activeOrderListsViewType) {
                    this.orderListsViewService.setOrderListsViewType(child.restrictToOrderListsViewType);
                    this.activeOrderListsViewType = child.restrictToOrderListsViewType;
                  }
                }
              }
            }

            if (!first) {
              return of([]);
            }
            return this.sortNavbarItemsByRole(this.parentElement);
          }),
        )
        .subscribe((elements) => {
          this.elements = elements.filter((item) => {
            if (item.hideFromMenu) {
              return false;
            }

            const hiddenIds: OrderListRouteType[] = [];

            if (!this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_OFFERS)) {
              hiddenIds.push(OrderListRouteType.OFFERS);
            }

            if (!this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_ORDERS)) {
              hiddenIds.push(
                OrderListRouteType.ACTIVE_ORDERS,
                OrderListRouteType.DONE,
                OrderListRouteType.PLANNED_LOADINGS,
                OrderListRouteType.FINISHED_LOADINGS,
              );
            }

            // Ids are unique only within a parent, so match them against the parent they belong to
            if (this.parentElement.id === RouteTypeEnums.ORDERS && hiddenIds.includes(item.id as OrderListRouteType)) {
              return false;
            }

            const hiddenInquiriesIds: InquiriesRouteType[] = [];

            if (!this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_PROJECT_RESERVATIONS)) {
              hiddenInquiriesIds.push(InquiriesRouteType.PROJECT_RESERVATIONS);
            }

            if (!this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_CMPR)) {
              hiddenInquiriesIds.push(InquiriesRouteType.CUSTOM_MADE_PRICE_REQUESTS, InquiriesRouteType.CUSTOM_MADE_PRICE_ESTIMATES);
            }

            if (!this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_CUSTOM_MADE_PRICE_ESTIMATES)) {
              hiddenInquiriesIds.push(InquiriesRouteType.CUSTOM_MADE_PRICE_ESTIMATES);
            }

            if (!this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_AND_CREATE_CLAIMS)) {
              hiddenInquiriesIds.push(InquiriesRouteType.CLAIMS);
            }

            if (this.parentElement.id === RouteTypeEnums.INQUIRIES && hiddenInquiriesIds.includes(item.id as InquiriesRouteType)) {
              return false;
            }

            if (item.restrictToOrderListsViewType) {
              return item.restrictToOrderListsViewType === this.activeOrderListsViewType;
            }
            return true;
          });

          if (this.navigateAfterViewSwitch) {
            this.navigateAfterViewSwitch = false;
            const firstAvailable = this.elements[0];
            if (firstAvailable) {
              // Determine base URL path
              const base = this.topLevelUrlPath.startsWith('/') ? this.topLevelUrlPath : `/${this.topLevelUrlPath}`;
              this.router.navigate([base, firstAvailable.path]);
            }
          }

          if (this.parentElement?.id === RouteTypeEnums.ORDERS && this.selectedElement?.id !== OrderListRouteType.SEARCH) {
            this.ensureSelectedElementVisible();
          }

          if (!this.thirdLevelVisible) {
            if (this.selectedElementUrlPath?.length) {
              this.selectedElement =
                this.elements.find(
                  (element) => element.path === this.selectedElementUrlPath || element.aliasPaths?.includes(this.selectedElementUrlPath),
                ) || null;
            }
          }
        }),
    );
  }

  // if the selected element is not visible in the current view,
  // try to find an alternate match based on the current URL path or alias paths,
  // and navigate to it if found
  ensureSelectedElementVisible() {
    const isInView = this.elements.some((e) => e.id === this.selectedElement?.id);
    if (!isInView) {
      const urlParts = this.router.url.split('?')[0].split('/');
      const pathPart = urlParts[urlParts.length > 3 ? 2 : Math.max(1, urlParts.length - 1)]; // Usually /orders/xxx/...

      const alternateMatch = this.elements.find(
        (e) =>
          e.path === this.selectedElement?.path ||
          e.aliasPaths?.includes(this.selectedElement?.path as any) ||
          (pathPart && (e.path === pathPart || e.aliasPaths?.includes(pathPart as any))),
      );
      if (alternateMatch) {
        this.selectedElement = alternateMatch;
      } else if (!this.navigateAfterViewSwitch && this.elements.length > 0) {
        const firstAvailable = this.elements[0];
        const base = this.topLevelUrlPath.startsWith('/') ? this.topLevelUrlPath : `/${this.topLevelUrlPath}`;
        this.router.navigate([base, firstAvailable.path]);
      }
    }
  }

  onDropdownClick(subNavbarElement: NavbarElementInterface, event: MouseEvent) {
    event.preventDefault();

    const { second, third } = this.treeService.getStateValue();

    if (subNavbarElement === second && third) {
      this.treeService.closeNavbar('third');
      return;
    }

    this.treeService.open(null, subNavbarElement, false);
    this.treeService.modifyState({
      third: true,
    });
  }

  onNavbarClose() {
    this.treeService.closeNavbar('third');
  }

  isEstimatesElement(element: NavbarElementInterface): boolean {
    return this.parentElement?.id === RouteTypeEnums.INQUIRIES && element.id === InquiriesRouteType.CUSTOM_MADE_PRICE_ESTIMATES;
  }

  dismissEstimatesIntro(): void {
    this.customMadePriceEstimatesIntroService.dismiss();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.treeService.closeNavbar('second');
  }

  private sortNavbarItemsByRole(parentElement: NavbarElementInterface): Observable<NavbarElementInterface[]> {
    let elements = [];
    return this.userService.fromStorage().pipe(
      map((user) => {
        const parent = NavbarElements.find((navbarElement: NavbarElementInterface) => {
          return navbarElement.id === parentElement.id;
        });

        const children = parent.children.filter((child: NavbarElementInterface) => {
          return !child.allowedRoles || (child.allowedRoles && child.allowedRoles.includes(user.role.name));
        });

        if (parent.sortable) {
          elements = children.sort((current: NavbarElementInterface, next: NavbarElementInterface) => {
            const currentPosition = current.positions.find((position) => position.role === user.role.name);
            const nextPosition = next.positions.find((position) => position.role === user.role.name);

            return currentPosition.position - nextPosition.position;
          });
        } else {
          elements = children;
        }

        return elements;
      }),
    );
  }

  showSearch() {
    return this.searchService.hasSearchService();
  }

  showOrderListsViewTypeSwitch(): boolean {
    if (this.parentElement?.id !== RouteTypeEnums.ORDERS) {
      return false;
    }

    return !this.userService.isNarbutasLT() && this.permissionsService.hasPermission(UserPermissionsEnum.VIEW_ORDERS);
  }
}
